import { CopyOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons"
import { Alert, App, Button, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { RichTextViewer } from "../../../../components/RichTextEditor/RichTextViewer.tsx"
import { escapeHtml } from "../../../../components/RichTextEditor/rich-text-html.ts"
import { getEmailTemplates } from "../../../EmailTemplates/api/services.ts"
import { htmlToPlainText } from "../../../EmailTemplates/helpers/html-to-plain-text.ts"
import { type EmailTemplate } from "../../../EmailTemplates/types/email-template.types.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import SelectComponent from "../../../../components/Select/index.tsx"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"

const { Text } = Typography

/**
 * Company-wide list, not lead-scoped — fetched independently of the Lead's
 * own record. Read/select/copy access rides the Lead-view permission that
 * already gates this whole tab; only the empty-state's "Create Email
 * Template" link is gated on its own (settings) permission.
 */
function LeadOverviewEmailTemplatesCard() {
  const { message } = App.useApp()
  const canCreate = useAppSelector(
    (state) =>
      !!state.auth.permissions?.codes?.settings?.email_templates
        ?.settings_email_template_create
  )

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)

  const loadTemplates = async () => {
    setLoading(true)
    setLoadFailed(false)
    // Matches EmailTemplatesPage's own call — the backend's default page
    // size (10) is smaller than the 6 seeded defaults every team starts
    // with, so an unbounded call here would silently truncate the picker.
    const response = await getEmailTemplates({ page_size: 1000 })
    if (!response.ok) {
      setLoadFailed(true)
      setLoading(false)
      return
    }
    setTemplates(response.data?.results ?? [])
    setLoading(false)
  }

  useEffect(() => {
    void loadTemplates()
  }, [])

  const selected = templates.find((t) => t.id === selectedId)

  // Copy Subject alone — always plain text.
  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text)
      void message.success(successMessage)
    } catch {
      void message.error("Couldn't copy to clipboard. Please try again.")
    }
  }

  // Copy Email / Copy All — the body is real HTML now, so a plain
  // `writeText` would paste literal `<p>...</p>` markup into whatever the
  // user pastes into. Where the browser supports it, write both a
  // `text/html` and a `text/plain` form in one clipboard item, so a rich
  // compose window (Gmail, Outlook web) keeps the formatting and a plain
  // target still gets readable text — never a false success either way.
  const copyRichToClipboard = async (html: string, successMessage: string) => {
    try {
      if (navigator.clipboard.write && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([htmlToPlainText(html)], { type: "text/plain" })
          })
        ])
      } else {
        await navigator.clipboard.writeText(htmlToPlainText(html))
      }
      void message.success(successMessage)
    } catch {
      void message.error("Couldn't copy to clipboard. Please try again.")
    }
  }

  return (
    <WorkspaceCard
      cardId={"email-templates"}
      title={
        <OverviewCardTitle icon={<MailOutlined />} accent={"teal"}>
          Email Template
        </OverviewCardTitle>
      }
      ariaTitle={"Email Template"}
      className={"h-full"}
    >
      {loading ? (
        <Skeleton active title={false} paragraph={{ rows: 2 }} />
      ) : loadFailed ? (
        <Alert
          type={"error"}
          showIcon
          description={"Something went wrong loading email templates."}
          action={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void loadTemplates()}
              size={"small"}
            >
              Retry
            </Button>
          }
        />
      ) : templates.length === 0 ? (
        <Flex vertical gap={4} align={"start"}>
          <Text type={"secondary"}>No email templates are available.</Text>
          {canCreate && (
            <Link to={RoutesPaths.settings.emailTemplates}>
              <Button type={"link"} className={"!px-0"}>
                Create Email Template
              </Button>
            </Link>
          )}
        </Flex>
      ) : (
        <Flex vertical gap={"small"}>
          <SelectComponent
            showSearch
            allowClear
            placeholder={"Select Template"}
            value={selectedId}
            onChange={(value) => setSelectedId(value as string | undefined)}
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            style={{ width: "100%" }}
          />

          {selected && (
            <>
              <div>
                <Text type={"secondary"} className={"text-xs"}>
                  Subject
                </Text>
                <div>{selected.subject}</div>
              </div>

              <div>
                <Text type={"secondary"} className={"text-xs"}>
                  Email Body
                </Text>
                <RichTextViewer
                  html={selected.body}
                  allowImages
                  className={
                    "max-h-40 overflow-y-auto rounded border border-border p-2 text-sm"
                  }
                />
              </div>

              <Flex gap={"small"} wrap>
                <Button
                  size={"small"}
                  onClick={() => copyToClipboard(selected.subject, "Subject copied")}
                >
                  Copy Subject
                </Button>
                <Button
                  size={"small"}
                  onClick={() => copyRichToClipboard(selected.body, "Email copied")}
                >
                  Copy Email
                </Button>
                <Button
                  size={"small"}
                  type={"primary"}
                  icon={<CopyOutlined />}
                  onClick={() =>
                    // The subject is free-typed plain text, not editor
                    // output, so it's escaped directly here rather than
                    // through `toDisplayHtml` — that helper's "does this
                    // look like real HTML" heuristic can misfire on a
                    // subject that merely contains a substring like "<b>",
                    // treating the whole line as pre-formatted and passing
                    // it through unescaped.
                    copyRichToClipboard(
                      `<p>${escapeHtml(`Subject: ${selected.subject}`)}</p>${selected.body}`,
                      "Email content copied"
                    )
                  }
                >
                  Copy All
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      )}
    </WorkspaceCard>
  )
}

export { LeadOverviewEmailTemplatesCard }
